/**
 * Navigation Configuration
 * 
 * Centralized navigation items for all breakpoints.
 * Single source of truth for tabs (mobile) and sidebar (tablet/desktop).
 * 
 * @see docs/design/01_MOBILE_SPEC.md
 * @see docs/design/02_TABLET_SPEC.md
 * @see docs/design/03_DESKTOP_SPEC.md
 */

import { Ionicons } from '@expo/vector-icons';

// =============================================================================
// TYPES
// =============================================================================

export type NavItemId = 'home' | 'portfolio' | 'map' | 'activity' | 'settings' | 'admin';

export interface NavItem {
  id: NavItemId;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  iconActive: keyof typeof Ionicons.glyphMap;
  /** Route path for navigation */
  route: string;
  /** If true, only visible to admin users */
  adminOnly?: boolean;
  /** If true, shown in secondary section (desktop sidebar) */
  secondary?: boolean;
}

// =============================================================================
// NAVIGATION ITEMS (Authoritative)
// =============================================================================

/**
 * Primary navigation items - shown on all breakpoints
 * Order matters: Home → Portfolio → Map → Activity → Settings
 */
export const PRIMARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'home-outline',
    iconActive: 'home',
    route: '/v2/home',
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    icon: 'folder-outline',
    iconActive: 'folder',
    route: '/v2/portfolio',
  },
  {
    id: 'map',
    label: 'Map',
    icon: 'map-outline',
    iconActive: 'map',
    route: '/v2/map',
  },
  {
    id: 'activity',
    label: 'Activity',
    icon: 'notifications-outline',
    iconActive: 'notifications',
    route: '/v2/activity',
  },
];

/**
 * Secondary navigation items - shown below divider
 * Settings always visible, Admin only for admin users
 */
export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings-outline',
    iconActive: 'settings',
    route: '/v2/settings',
    secondary: true,
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: 'shield-outline',
    iconActive: 'shield',
    route: '/v2/admin',
    adminOnly: true,
    secondary: true,
  },
];

/**
 * All navigation items combined
 */
export const ALL_NAV_ITEMS: NavItem[] = [...PRIMARY_NAV_ITEMS, ...SECONDARY_NAV_ITEMS];

/**
 * Mobile bottom tabs (5 max, no admin - admin is inside settings on mobile)
 */
export const MOBILE_TAB_ITEMS: NavItem[] = [
  ...PRIMARY_NAV_ITEMS,
  SECONDARY_NAV_ITEMS.find(item => item.id === 'settings')!,
];

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get nav item by ID
 */
export function getNavItem(id: NavItemId): NavItem | undefined {
  return ALL_NAV_ITEMS.find(item => item.id === id);
}

/**
 * Filter nav items based on user role
 */
export function filterNavItems(items: NavItem[], isAdmin: boolean): NavItem[] {
  return items.filter(item => !item.adminOnly || isAdmin);
}

